import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Activities: React.FC = () => {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/pages`);
      if (response.data.success) {
        setSettings(response.data.data.activitiesPage);
      }
    } catch (error) {
      console.error('Error fetching activities settings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  const activities = [
    {
      key: 'football',
      path: '/activities/football',
      icon: '⚽',
      title: 'Football',
      description: 'Weekly sessions'
    },
    {
      key: 'walkAndTalk',
      path: '/activities/walk-and-talk',
      icon: '🚶',
      title: 'Walk & Talk',
      description: 'Stay active together'
    },
    {
      key: 'womensCoffeeMornings',
      path: '/activities/womens-coffee-mornings',
      icon: '☕',
      title: 'Coffee Mornings',
      description: 'Women\'s social events'
    },
    {
      key: 'womensAerobics',
      path: '/activities/womens-aerobics',
      icon: '🤸',
      title: 'Women\'s Aerobics',
      description: 'Fitness sessions'
    },
    {
      key: 'cycling',
      path: '/activities/cycling',
      icon: '🚴',
      title: 'Cycling',
      description: 'Group rides'
    }
  ];

  // Filter activities based on visibility settings
  const visibleActivities = activities.filter(
    activity => settings?.showActivities?.[activity.key] !== false
  );

  const heroStyle = settings?.hero?.backgroundImage
    ? {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(http://localhost:5000${settings.hero.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    : {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="hero-section text-center text-white py-24" style={heroStyle}>
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4">
            {settings?.hero?.title || 'Our Activities'}
          </h1>
          <p className="text-xl mb-8">
            {settings?.hero?.subtitle || 'Join us for a variety of sports and activities that bring our community together'}
          </p>
          <div className="flex gap-4 justify-center">
            {settings?.hero?.buttonText && (
              <Link
                to="/membership"
                className="btn-primary"
              >
                {settings.hero.buttonText}
              </Link>
            )}
            {settings?.hero?.secondaryButtonText && (
              <Link
                to="/community-hub"
                className="btn-secondary"
              >
                {settings.hero.secondaryButtonText}
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Description Section */}
      {settings?.description && (
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-lg text-gray-700 text-center leading-relaxed">
              {settings.description}
            </p>
          </div>
        </section>
      )}

      {/* Image Gallery Section */}
      {settings?.images && settings.images.length > 0 && (
        <section className="py-12 bg-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Activity Gallery</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {settings.images.map((img: string, idx: number) => (
                <div key={idx} className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition">
                  <img
                    src={`http://localhost:5000${img}`}
                    alt={`Activity ${idx + 1}`}
                    className="w-full h-64 object-cover hover:scale-110 transition duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Activities Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {visibleActivities.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {visibleActivities.map((activity) => (
                <Link
                  key={activity.key}
                  to={activity.path}
                  className="bg-white rounded-lg shadow-md p-8 text-center hover:transform hover:scale-105 transition duration-300 hover:shadow-xl"
                >
                  <div className="text-6xl mb-4">{activity.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{activity.title}</h3>
                  <p className="text-gray-600">{activity.description}</p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-600 py-12">
              <p className="text-xl">No activities available at the moment.</p>
              <p className="text-sm mt-2">Check back soon for updates!</p>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-primary text-white text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Involved?</h2>
          <p className="text-xl mb-8">
            Become a member today and join any of our activities!
          </p>
          <Link
            to="/membership"
            className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition inline-block"
          >
            Join Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Activities;
